import type { JsonPrimitive, JsonValue } from 'type-fest'
import { z } from 'zod'

import type {
  NonEmptyArray,
  PositiveInteger,
  ZodInfer,
} from '@mntn-dev/utility-types'
import { EnumBuilder } from '@mntn-dev/utility-types'

import { coerceDate } from '../preprocessors.ts'
import { validatePhoneNumber } from '../utils/phone-number/validation.ts'

/**
 * NonEmptyString
 */
export const NonEmptyStringSchema = <Max extends number, Min extends number>(
  max?: PositiveInteger<Max>,
  min?: PositiveInteger<Min>,
  messages?: {
    max?: string
    min?: string
  }
) => {
  const nonEmptyString = z
    .string()
    .trim()
    .min(min ?? 1, messages?.min)
  return max ? nonEmptyString.max(max, messages?.max) : nonEmptyString
}

/**
 * Optional NonEmptyString
 */
export const OptionalNonEmptyStringSchema = <
  Max extends number,
  Min extends number,
>(
  max?: PositiveInteger<Max>,
  min?: PositiveInteger<Min>,
  messages?: {
    max?: string
    min?: string
  }
) => {
  let schema = z.string().trim()

  // only include a min if its greater than 1,
  // otherwise an unintended min error will happen on empty string
  if (min && min > 1) {
    schema = schema.min(min, messages?.min)
  }

  if (max) {
    schema = schema.max(max, messages?.max)
  }

  return schema.transform((val) => (val === '' ? undefined : val)).optional()
}

/**
 * AgreementCheck
 */
export const AgreementCheckSchema = z.object({
  agreementCheck: z.boolean(),
})

export type AgreementCheck = ZodInfer<typeof AgreementCheckSchema>

/**
 * PositiveInteger
 */
export const PositiveIntegerSchema = z.int().positive()

/**
 * PhoneNumber
 */
export const PhoneNumberSchema = z.string().trim().min(7).max(20)
export type PhoneNumber = ZodInfer<typeof PhoneNumberSchema>

/**
 * CompanyName
 */
export const CompanyNameSchema = NonEmptyStringSchema(256)
export type CompanyName = ZodInfer<typeof CompanyNameSchema>

/**
 * Decimal
 *
 * A number that is rounded to a specific precision
 */
export const DecimalSchema = <Precision extends number>(
  decimals?: PositiveInteger<Precision>,
  schema: z.ZodNumber = z.number()
) => {
  return schema.transform((value) => {
    const precision = 10 ** (decimals ?? 0)
    return Math.floor(value * precision) / precision
  })
}

export type Decimal = ZodInfer<ReturnType<typeof DecimalSchema>>

/**
 * DollarsSchema: a 2-decimal floating point value representing USD
 */
export const DollarsSchema = <Max extends number>(
  max?: PositiveInteger<Max>
) => {
  const schema = z.number().nonnegative()
  return (max !== undefined ? schema.max(max) : schema).transform(
    (val) => Math.round(val * 100) / 100
  )
}

export type Dollars = ZodInfer<ReturnType<typeof DollarsSchema>>

/**
 * CountSchema
 */
export const CountSchema = z.int().nonnegative()
export type Count = ZodInfer<typeof CountSchema>

/**
 * CostMarginPercent
 */
export const CostMarginPercentSchema = z.int().nonnegative().max(99)
export type CostMarginPercent = ZodInfer<typeof CostMarginPercentSchema>

/**
 * DataOrigin - This is used to track the origin of a data
 * that was imported into this application from an external
 * source.
 */
export const [DataOrigins, DataOriginSchema, DataOriginEnum] =
  EnumBuilder('qontrol')

export type DataOrigin = ZodInfer<typeof DataOriginSchema>
/**
 * Date (or ISO8601 string)
 */
export const IsoDateStringSchema = z.iso.date()
export type IsoDateString = ZodInfer<typeof IsoDateStringSchema>

export const IsoDateTimeStringSchema = z.iso.datetime()
export type IsoDateTimeString = ZodInfer<typeof IsoDateTimeStringSchema>

export const DateSchema = z.union([
  z.date(),
  z
    .union([IsoDateStringSchema, IsoDateTimeStringSchema])
    .transform((value) => new Date(value)),
])

/**
 * Description
 */
export const DescriptionSchema = NonEmptyStringSchema()
export type Description = ZodInfer<typeof DescriptionSchema>

/**
 * EmailAddress
 */
export const EmailAddressSchema = z.email().trim()
export type EmailAddress = ZodInfer<typeof EmailAddressSchema>

/**
 * LanguageId
 */
export const [LanguageIds, LanguageIdSchema, LanguageIdEnum] = EnumBuilder('en')
export type LanguageId = ZodInfer<typeof LanguageIdSchema>

/**
 * Name
 */
export const MaxNameLength = 256
export const NameSchema = NonEmptyStringSchema(MaxNameLength)
export type Name = ZodInfer<typeof NameSchema>

/**
 * Note
 */
export const MaxNoteLength = 50_000
export const MeaningfulNoteLength = 100

export const NoteSchema = NonEmptyStringSchema(MaxNoteLength)
export type Note = ZodInfer<typeof NoteSchema>

export const OptionalNoteSchema = z
  .string()
  .trim()
  .max(MaxNoteLength)
  .optional()
export type OptionalNote = ZodInfer<typeof OptionalNoteSchema>

export const MeaningfulNoteSchema = NonEmptyStringSchema(
  MaxNoteLength,
  MeaningfulNoteLength
)
export type MeaningfulNote = ZodInfer<typeof MeaningfulNoteSchema>

/**
 * Percentage value between 0 and 100 (inclusive).
 * This schema validates integer percentage values in that range.
 */
export const PercentageSchema = z.int().min(0).max(100)

/**
 * Phone Number
 */
const e164rx = /^\+\d+$/

const E164PhoneNumberSchema = z
  .string()
  .trim()
  .refine((value) => {
    return typeof value === 'string' ? e164rx.test(value) : false
  })

export const USPhoneNumberSchema = E164PhoneNumberSchema.superRefine(
  (value, ctx) => {
    if (value.length > 12) {
      ctx.addIssue({
        code: 'too_big',
        maximum: 12,
        origin: 'string',
        inclusive: true,
        message: 'US phone numbers must be 12 characters long (+1XXXXXXXXXX)',
      })
    }

    if (value.length < 12) {
      ctx.addIssue({
        code: 'too_small',
        minimum: 12,
        origin: 'string',
        inclusive: true,
        message: 'US phone numbers must be 12 characters long (+1XXXXXXXXXX)',
      })
    }

    const results = validatePhoneNumber(value)

    if (results === 'invalid-country-code') {
      ctx.addIssue({
        code: 'custom',
        message: 'Only US phone numbers are currently supported',
      })
    }

    if (results !== 'valid') {
      ctx.addIssue({
        code: 'custom',
        message: 'Invalid US phone number',
      })
    }
  }
)

export type USPhoneNumberSchema = ZodInfer<typeof USPhoneNumberSchema>

/**
 * ProposalVersion
 */
export const ProposalVersionSchema = z.int().nonnegative()
export type ProposalVersion = ZodInfer<typeof ProposalVersionSchema>

/**
 * Timestamp
 */
export const TimestampSchema = z.unknown().transform(coerceDate).pipe(z.date())
export type Timestamp = ZodInfer<typeof TimestampSchema>

/**
 * Translations
 */
export const TranslationsSchema = <Properties extends NonEmptyArray<string>>(
  ...properties: Properties
) =>
  z.partialRecord(
    LanguageIdSchema,
    z.partialRecord(z.enum(properties), NonEmptyStringSchema())
  )

/**
 * UnixTimestamp
 */
export const UnixTimestampSchema = z
  .int()
  .positive()
  .refine(
    (val) => {
      return val >= 0 && val <= 2147483647 // Optional range check (32-bit max timestamp)
    },
    {
      error: 'Invalid Unix timestamp',
    }
  )

export type UnixTimestamp = ZodInfer<typeof UnixTimestampSchema>

/**
 * Url
 */
export const UrlSchema = z.url().trim()
export type Url = ZodInfer<typeof UrlSchema>

/**
 * JsonValue
 * Inspired by https://zod.dev/?id=json-type
 *
 * In zod4, this can be replaced z.json()
 */
export const JsonPrimitiveSchema: z.ZodType<JsonPrimitive> = z.union([
  z.string(),
  z.number(),
  z.boolean(),
  z.null(),
])

export const JsonValueSchema: z.ZodType<JsonValue> = z.lazy(() =>
  z.union([
    // JsonPrimitive
    JsonPrimitiveSchema,
    // JsonObject
    z.record(z.string(), JsonValueSchema),
    // JsonArray
    z.array(JsonValueSchema),
  ])
)

/**
 * Byte sizes
 */
export const KB = 1024
export const MB = 1024 * KB
export const GB = 1024 * MB
